import React, { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./audiences.module.css";
import { Button, Section, SectionIntro } from "../../../../COMPONENTS/UI";
import { audiences } from "../../../../CONTENT/home";
import { EASE } from "../../../../UTILS/ANIMATION/motionVariants";

const AudienceSwitcher = () => {
  const [activeId, setActiveId] = useState(audiences[0].id);
  const tabRefs = useRef([]);
  const activeIndex = audiences.findIndex((a) => a.id === activeId);
  const active = audiences[activeIndex];

  const select = (index) => {
    const next = (index + audiences.length) % audiences.length;
    setActiveId(audiences[next].id);
    tabRefs.current[next]?.focus();
  };

  const onKeyDown = (e) => {
    const keys = {
      ArrowDown: activeIndex + 1,
      ArrowRight: activeIndex + 1,
      ArrowUp: activeIndex - 1,
      ArrowLeft: activeIndex - 1,
      Home: 0,
      End: audiences.length - 1,
    };
    if (e.key in keys) {
      e.preventDefault();
      select(keys[e.key]);
    }
  };

  return (
    <Section tone="cloud" id="audiences" labelledBy="audiences-title">
      <SectionIntro
        id="audiences-title"
        eyebrow="Who KUR serves"
        title="One network, three ways in."
        lead="Sponsors, physicians and patients each meet KUR differently. Choose your path."
      />

      <div className={styles.layout}>
        <div
          className={styles.tabs}
          role="tablist"
          aria-label="Audience"
          aria-orientation="vertical"
          onKeyDown={onKeyDown}
        >
          {audiences.map((a, i) => (
            <button
              key={a.id}
              ref={(el) => (tabRefs.current[i] = el)}
              id={`aud-tab-${a.id}`}
              role="tab"
              type="button"
              aria-selected={a.id === activeId}
              aria-controls={`aud-panel-${a.id}`}
              tabIndex={a.id === activeId ? 0 : -1}
              className={styles.tab}
              onClick={() => setActiveId(a.id)}
            >
              <span className={styles.num} aria-hidden="true">
                0{i + 1}
              </span>
              <span className={styles.tabLabel}>{a.label}</span>
            </button>
          ))}
        </div>

        <div
          className={styles.panel}
          role="tabpanel"
          id={`aud-panel-${active.id}`}
          aria-labelledby={`aud-tab-${active.id}`}
          data-audience={active.id}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={active.id}
              className={styles.panelInner}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <div className={styles.copy}>
                <h3 className={styles.heading}>{active.title}</h3>
                <p className={styles.description}>{active.description}</p>
                <ul className={styles.benefits}>
                  {active.benefits.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
                <Button href={active.cta.href} variant="link" tone="light">
                  {active.cta.label}
                </Button>
              </div>
              <div className={styles.media}>
                <img src={active.image} alt={active.alt} loading="lazy" />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Section>
  );
};

export default AudienceSwitcher;
